import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { isEqual } from 'lodash';
import { useMutation } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';
import Collapse from 'react-collapse';
import {
  Avatar,
  Card,
  DisplayText,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';
import { Tooltip } from 'react-clone-tippy';
import {
  ALL_MODULES,
  ApiProgressLoader,
  AppContext,
  AttachmentPreviewer,
  DateTimeFormatter,
  generateString,
  getCurrentUserHook,
  getTranslatedText,
  TICKET_SOURCE,
  withErrorBoundary
} from '@nile/components';
import Recepients from '@nile/mangifera/src/Components/Chat/Recepients';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import {
  dompurifier,
  getDownloadURL
} from '@nile/components/src/Utils/helpers';
import { RESEND_TICKET_CONVERSATION } from '@nile/graphql';
import { RESEND_TICKET_INTEGRATION_CONVERSATION } from '@nile/graphql/src/Mutation/ticket';

let showEmailSentTimer;

const ThreadConversationBubble = (props) => {
  const domain = window.location.host;

  const { data, ticketId, ticketSource, readOnly, showEmailSent } = props;
  const messageType = data?.threadConversation?.type;
  const htmlData = data?.threadConversation?.content;
  let htmlToDisplay = dompurifier(htmlData, {
    FORBID_TAGS: ['style']
  });
  htmlToDisplay = htmlToDisplay.replaceAll('<img', "<img loading='lazy'");
  const randomId = generateString(5) || 'abcde';
  const attachments = data?.threadConversation?.attachments || [];

  const currentUser = getCurrentUserHook();
  const intl = useIntl();
  const appContext = useContext(AppContext);

  const [senderUser, setSenderUser] = useState(null);
  const [isBubbleOpen, setIsBubbleOpen] = useState(false);
  const [conversationStatus, setConversationStatus] = useState(null);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [formatterAttachment, setFormatterAttachment] = useState([]);

  const isMyChat = currentUser?.userId === senderUser?.user?.userId;
  const isStatusPending = useMemo(
    () => conversationStatus && conversationStatus !== 'SENT',
    [conversationStatus]
  );

  useEffect(() => {
    if (showEmailSent) {
      if (showEmailSentTimer) clearTimeout(showEmailSentTimer);
      showEmailSentTimer = setTimeout(() => {
        updateConvoStatus('SENT');
      }, 900);
    }
  }, [showEmailSent]);

  useEffect(() => {
    if (data?.threadConversation?.recipientDetails) {
      const senderDetails =
        data?.threadConversation?.recipientDetails?.filter(
          (item) => item.conversationRecipientType === 'SENDER'
        )?.[0] || null;
      setSenderUser(senderDetails);
    }
    if (data?.threadConversation?.conversationStatus)
      updateConvoStatus(data?.threadConversation?.conversationStatus);
  }, [
    data?.threadConversation?.recipientDetails,
    data?.threadConversation?.conversationStatus
  ]);

  const updateConvoStatus = (conversationThreadStatus) => {
    if (!isEqual(conversationStatus, conversationThreadStatus)) {
      if (conversationThreadStatus === 'SENT') {
        setConversationStatus(null);
      } else {
        setConversationStatus(conversationThreadStatus);
      }
    }
  };

  const resendConversationQuery = useMemo(() => {
    return ticketSource && ticketSource === TICKET_SOURCE.Communication
      ? RESEND_TICKET_INTEGRATION_CONVERSATION
      : RESEND_TICKET_CONVERSATION;
  }, [ticketSource]);

  const [
    resendTicketConversation,
    { loading: resendTicketConversationLoading }
  ] = useMutation(resendConversationQuery, {
    onCompleted: (res) => {
      if (res?.resendTicketConversation?.conversationStatus)
        updateConvoStatus(res?.resendTicketConversation?.conversationStatus);
    }
  });

  const getInitial = useCallback(() => {
    if (senderUser?.user?.name) {
      return senderUser.user.name?.[0];
    } else if (senderUser?.label) {
      return senderUser.label?.[0];
    }
  }, [senderUser, messageType]);

  const initial = getInitial();

  const isEmailNotification = !initial && messageType !== 'DESCRIPTION';

  const conversationBubbleWrapperClassNames = [
    'flex g10 bubble-wrapper',
    isMyChat && 'frow-reverse',
    'px10'
  ]
    .filter(Boolean)
    .join(' ');

  const bubbleOuterClassNames = ['w100'].filter(Boolean).join(' ');

  const bubblePadding = isEmailNotification
    ? 'pt20 pb20 px10'
    : 'pt20 pb20 px10';

  const conversationBubbleClassNames = ['word-break br20', bubblePadding]
    .filter(Boolean)
    .join(' ');

  const headerClassNames = ['bubble-header fsize12 flex actr fx-sb fg1']
    .filter(Boolean)
    .join(' ');

  const getAddedUser = useCallback(() => {
    if (senderUser?.user?.name) {
      return senderUser?.user?.name;
    } else if (senderUser?.label) {
      return senderUser?.label;
    } else if (messageType === 'NOTIFICATION') {
      return 'SuperBot';
    }
  }, [senderUser, messageType]);

  const addedUser = getAddedUser();

  const getConversationIcon = (type) => {
    switch (type) {
      case 'DESCRIPTION':
        return 'Reply';
      case 'VENDOR_REPLY':
        return 'helpcenter';
      case 'CHAT':
        return 'Chat Initiated by';
      default:
        return 'onboardingNotificationTemplate';
    }
  };

  const getHeaderMessageKey = (type) => {
    switch (type) {
      case 'REQ_NOTIFICATION':
        return 'ticket.details.emailNotificationSent.label';
      case 'DESCRIPTION':
        return 'ticket.details.postedBy';
      case 'CHAT':
        return 'IntercomChat';
      default:
        return 'ticket.details.chat.reply';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SENT':
        return <SVGIcon name="SuccessCircle" />;
      case 'FAILED':
        return <SVGIcon name="WarningOutline" color="red" />;
      default:
        return <ApiProgressLoader isLoading />;
    }
  };

  const getStatusMessageKey = (status) => {
    switch (status) {
      case 'SENT':
        return 'ticket.details.emailNotification.status.success';
      case 'FAILED':
        return 'app.common.message.not.delivered';
      default:
        return 'ticket.details.emailNotification.status.progress';
    }
  };

  const toggleIsOpen = useCallback(() => {
    setIsBubbleOpen(!isBubbleOpen);
  }, [isBubbleOpen]);

  const resendConversation = () => {
    resendTicketConversation({
      variables: {
        ticketId: ticketId,
        input: {
          conversationId: data?.threadConversation?.conversationId
        }
      }
    });
  };

  const getAttachmentURL = (item) => {
    const module = ALL_MODULES.ticketConversationAttachment;

    const details = {
      fileName: item?.fileName,
      conversationId: data?.threadConversation?.conversationId,
      ticketId: ticketId
    };

    const url = getDownloadURL({ module, details });
    return url;
  };

  function formatAttachmentForPreview(attachments, cb) {
    const formattedItems = attachments.map((item) => {
      if (item)
        return {
          name: item.originalFileName,
          url: getAttachmentURL(item),
          type: item.fileName.substr(
            item.fileName.lastIndexOf('.') + 1,
            item.fileName.length
          )
        };
    });
    setFormatterAttachment(formattedItems);
    cb();
  }

  function openAttachmentPreview(index, attachments, item) {
    if (attachments) {
      formatAttachmentForPreview(attachments, () => {
        setShowAttachmentPreview({
          ...showAttachmentPreview,
          show: true,
          index
        });
      });
    }
  }

  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  return (
    <div className={conversationBubbleWrapperClassNames}>
      <div id="conversation-bubble-outer" className={bubbleOuterClassNames}>
        <Card
          variant={isStatusPending ? 'card' : isMyChat ? 'discussion' : 'card'}
          padding="sizeExtraSmall"
          border={isStatusPending ? 'none' : 'none'}
        >
          <div
            id="conversation-bubble-inner"
            className={conversationBubbleClassNames}
          >
            <div className="flex g10 align-items-center">
              <div className="mr8">
                {messageType !== 'NOTIFICATION' ? ( //if bot notification then show superops icon in else statement
                  <Avatar
                    initials={initial}
                    size={'extraSmall'}
                    source={
                      !initial && messageType !== 'DESCRIPTION'
                        ? `${envConfig.imageRoot}svg/notification.svg`
                        : ''
                    }
                  />
                ) : (
                  <div className="flex bg-red br50pt p2">
                    <SVGIcon name="SuperopsLight" size="sm" />
                  </div>
                )}
              </div>
              <div className="fw">
                <div className={headerClassNames}>
                  <div className="added-by flex g5 actr">
                    {!senderUser && messageType !== 'NOTIFICATION' && (
                      <div className="flex mr-5">
                        <SVGIcon
                          name={getConversationIcon(messageType)}
                          size="sm"
                        />
                      </div>
                    )}
                    <div className="flex g3">
                      {messageType !== 'NOTIFICATION' && (
                        <div>
                          <FormattedMessage
                            id={getHeaderMessageKey(messageType)}
                          />
                        </div>
                      )}
                      {addedUser && (
                        <div>
                          <TextStyle variation="strong">{addedUser}</TextStyle>
                        </div>
                      )}
                    </div>
                    {!senderUser && (
                      <div onClick={toggleIsOpen} className="flex pointer ml4">
                        <SVGIcon name="MoreHorizontal" />
                      </div>
                    )}
                    <Tooltip
                      html={
                        <Recepients
                          message={{
                            receivers: data?.threadConversation?.recipientDetails.filter(
                              (item) =>
                                item.conversationRecipientType === 'RECEIVER'
                            )
                          }}
                        />
                      }
                      delay={300}
                      duration={250}
                      position="bottom-start"
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                      theme="super-light"
                      className="flex"
                    >
                      <div className="flex center ml-5 mb-2">
                        <SVGIcon name="DropSheet" size="sm" />
                      </div>
                    </Tooltip>
                  </div>
                  <div
                    key={conversationStatus}
                    className="flex g3 right-container actr"
                  >
                    {conversationStatus || showEmailSent ? (
                      <div className="fx actr g5">
                        {resendTicketConversationLoading ? (
                          <ApiProgressLoader isLoading />
                        ) : (
                          getStatusIcon(
                            showEmailSent ? 'SENT' : conversationStatus
                          )
                        )}
                        {conversationStatus === 'FAILED' &&
                        !resendTicketConversationLoading ? (
                          <Tooltip
                            html={
                              <>
                                {data?.threadConversation?.failureReason && (
                                  <div className="p10 rounded-4px fx fcol g5 bshadow">
                                    <div className="fx fcol g3">
                                      <DisplayText size="medium" weight="Bold">
                                        {getTranslatedText(
                                          intl,
                                          'common.error'
                                        )}
                                      </DisplayText>
                                      <DisplayText
                                        weight="Regular"
                                        size="small"
                                      >
                                        <TextStyle variation="subdued">
                                          <DateTimeFormatter
                                            type="dateTime"
                                            date={data?.time}
                                            company={appContext?.company}
                                          />
                                        </TextStyle>
                                      </DisplayText>
                                    </div>
                                    <DisplayText>
                                      {getTranslatedText(
                                        intl,
                                        `app.errorCode.${data?.threadConversation?.failureReason?.errorCode}`
                                      )}
                                    </DisplayText>
                                  </div>
                                )}
                              </>
                            }
                            delay={300}
                            duration={250}
                            position="bottom-start"
                            inertia
                            trigger="mouseenter"
                            animation="shift"
                            animateFill={false}
                            theme="super-light"
                          >
                            <TextStyle variation="subdued">
                              <FormattedMessage
                                id={
                                  resendTicketConversationLoading
                                    ? 'ticket.details.emailNotification.status.progress'
                                    : getStatusMessageKey(
                                        showEmailSent
                                          ? 'SENT'
                                          : conversationStatus
                                      )
                                }
                              />
                            </TextStyle>
                          </Tooltip>
                        ) : (
                          <TextStyle variation="subdued">
                            <FormattedMessage
                              id={
                                resendTicketConversationLoading
                                  ? 'ticket.details.emailNotification.status.progress'
                                  : getStatusMessageKey(
                                      showEmailSent
                                        ? 'SENT'
                                        : conversationStatus
                                    )
                              }
                            />
                          </TextStyle>
                        )}
                        {!resendTicketConversationLoading &&
                          isMyChat &&
                          conversationStatus === 'FAILED' && (
                            <div
                              onClick={resendConversation}
                              className="linkText"
                            >
                              <FormattedMessage id="common.retry" />
                            </div>
                          )}
                      </div>
                    ) : (
                      <>
                        {data?.threadConversation?.conversationScope ===
                          'PRIVATE' && (
                          <Tippy
                            position="bottom"
                            title={getTranslatedText(
                              intl,
                              'tooltip.message.privateChannel'
                            )}
                          >
                            <div className="ptr">
                              <SVGIcon name="PrivateArticlePlain" />
                            </div>
                          </Tippy>
                        )}
                        <TextStyle
                          variation="subdued"
                          title={
                            <DateTimeFormatter
                              type="dateTime"
                              date={data?.time}
                            />
                          }
                        >
                          <div className="flex">
                            <DateTimeFormatter
                              type="dateTime"
                              date={data?.time}
                            />
                          </div>
                        </TextStyle>
                      </>
                    )}
                  </div>
                </div>
                <Collapse
                  isOpened={
                    senderUser || messageType === 'DESCRIPTION' || isBubbleOpen
                  }
                >
                  <div className="w100">
                    <Card
                      noShadow
                      variant={
                        isStatusPending
                          ? 'none'
                          : messageType === 'NOTIFICATION'
                          ? 'secondaryCard'
                          : isMyChat
                          ? 'discussion'
                          : 'card'
                      }
                    >
                      <p
                        className={`message-content mb-10 mt-10 relative overflow-x-auto ${randomId} ${
                          messageType === 'NOTIFICATION' ? 'p10' : ''
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: htmlToDisplay
                        }}
                      ></p>
                      {data?.threadConversation?.minifiedContent && (
                        <a
                          href={`https://${domain}/#/minify/ticket/ticketconversation/${ticketId}/${data?.threadConversation?.conversationId}`}
                          target="_blank"
                        >
                          View All
                        </a>
                      )}
                      {attachments?.length > 0 && (
                        <div className="flex wrap mt-10 g10 attachments-container">
                          {attachments.map((item, index) => (
                            <AttachmentHolder
                              onClick={() =>
                                openAttachmentPreview(index, attachments, item)
                              }
                              fileName={item?.originalFileName || ''}
                              fontWeight="bolder"
                              padded
                            />
                          ))}
                        </div>
                      )}
                      {showAttachmentPreview.show && (
                        <AttachmentPreviewer
                          showPreviewer={showAttachmentPreview.show}
                          closePreviwer={closePreviwer}
                          list={formatterAttachment}
                          selectedIndex={showAttachmentPreview.index}
                        />
                      )}
                    </Card>
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default withErrorBoundary(withRouter(ThreadConversationBubble));
